// Centralized keys + helpers for the crew document-compliance flags:
//   - Contract For This Job — per (job × crew member), stored in the job's calculator crewPay
//     inputs (rides the existing calculator save path).
//   - ACH Info / W-9 — per crew member (global; apply to every interstate job), stored on the
//     User node's `metadata` and written via setCrewDocFlags (#7001), which authorizes the write
//     through the job rather than the person's zone. The server builds the metadata patch and
//     its *UpdatedAt companions; the key names are mirrored in backend
//     `user-manager/users.crewDocFlags.ts`.
//
// Everything the two UIs (the OTR Labor Tracking dashboard and the calculator crew-pay table)
// need to read/write these flags lives here, so a future migration of the per-user flags from
// User.metadata to the Fields system touches this module + the read points + the write path only.

// ── Per-user flags (User.metadata) ────────────────────────────────────────────

export const USER_DOC_FLAG_KEYS = {
    ach: 'otrAchInfo',
    w9: 'otrW9',
} as const;

export type UserDocFlag = keyof typeof USER_DOC_FLAG_KEYS;

/** Human-readable labels for the per-user flags (used in save-feedback toasts). */
export const USER_DOC_FLAG_LABELS: Record<UserDocFlag, string> = {
    ach: 'ACH Info',
    w9: 'W-9',
};

/**
 * Normalize a stored flag value to a boolean. Values may be a real boolean (written by the
 * calculator, which dispatches booleans) or the string 'true'/'false' (written by the
 * dashboard, which stages String(checked)) — accept both, mirroring the existing crewPay
 * `__enabled` convention.
 */
export function isDocFlagChecked(value: unknown): boolean {
    return value === true || value === 'true';
}

/** Read a per-user flag off a User.metadata map. Absent key → false (legacy/unset). */
export function readUserDocFlag(
    metadata: Record<string, unknown> | null | undefined,
    flag: UserDocFlag,
): boolean {
    return isDocFlagChecked(metadata?.[USER_DOC_FLAG_KEYS[flag]]);
}

/**
 * Requirement: `#7001` - guard a setCrewDocFlags result: the server answers with the number of
 * users it updated and errors rather than updating nobody, but never report "Saved" for anything
 * other than exactly one write.
 */
export function assertCrewDocFlagsSaved(updated: number | null | undefined): void {
    if (updated !== 1) {
        throw new Error('The document flag was not saved.');
    }
}

// ── Per-(job × crew member) Contract flag (job calculator crewPay inputs) ──────

/** crewPay sub-field name for the Contract flag. */
export const CONTRACT_FLAG = 'contractForThisJob';

/**
 * Calculator runtime input key for a crew member's Contract flag (crewPay__ flat namespace,
 * kept flat by FLAT_CALCULATOR_INPUT_PREFIXES). On job save this becomes the job metadata key
 * `calculatorInput__crewPay__<id>__contractForThisJob__enabled`.
 */
export function contractRuntimeEnabledKey(crewMemberId: string): string {
    return `crewPay__${crewMemberId}__${CONTRACT_FLAG}__enabled`;
}

export function contractRuntimeUpdatedAtKey(crewMemberId: string): string {
    return `crewPay__${crewMemberId}__${CONTRACT_FLAG}__updatedAt`;
}

// ── Per-(job × crew member) "Pay Labor to FP" override (#6538) ─────────────────
// Deliberately NOT one of the document-compliance flags above: those record paperwork, this one
// changes money. It only shares their key shape because both are per-(job × crew member)
// calculator inputs that must stay in the flat `crewPay__` namespace.

/** crewPay sub-field name for the manual "pay this member's labor to the FP" override. */
export const PAY_TO_FP_FLAG = 'payToFp';

/**
 * Calculator runtime input key for a crew member's "Pay Labor to FP" override. On job save this
 * becomes the job metadata key `calculatorInput__crewPay__<id>__payToFp__enabled`, which the
 * calculator reads in section 11d to zero that member's amounts and forward the money to the FP.
 */
export function payToFpRuntimeEnabledKey(crewMemberId: string): string {
    return `crewPay__${crewMemberId}__${PAY_TO_FP_FLAG}__enabled`;
}

export function payToFpRuntimeUpdatedAtKey(crewMemberId: string): string {
    return `crewPay__${crewMemberId}__${PAY_TO_FP_FLAG}__updatedAt`;
}

/** Current time in epoch seconds (the unit used by the *UpdatedAt companion keys). */
export function nowSeconds(): number {
    return Math.floor(Date.now() / 1000);
}
